import { ScreenEffects } from '../common/screen-effects.ts';
import { io, type Socket } from 'socket.io-client';
import type { AchievementView } from '../../shared/achievements.ts';
import type { AchievementsPanel } from './achievements-panel.ts';
import { RUN_STEP_MS, WALK_STEP_MS, type CharacterDesign, type CharacterOutfit } from '../../shared/characters.ts';
import { currentCells, SLIDE_STEP_MS, slideCells, speedCells } from '../../shared/buff-zones.ts';
import { elementTypes } from '../../shared/elements.ts';
import { abilitiesOf, equippedLight, type ItemId } from '../../shared/items.ts';
import { itemCatalog } from '../common/item-catalog.ts';
import { animationCatalog } from '../common/battle-animations.ts';
import type {
  AbilityAnimation, FollowerView, BattleAction, BattleSnapshot, ClientToServerEvents, Direction, InspectedPlayer, Inventory, MapView, NpcView, OwnedMonsterView, PokedexView, Position,
  PlayerView, QuestLogEntry, ServerToClientEvents, TeamAction, TrainerCardView, VehicleRide, WarpPreview, WarpPreviews, WorldSnapshot, ZoneProgressMap,
} from '../../shared/protocol.ts';
import { facingCell, oppositeDirection } from '../../shared/protocol.ts';
import { coveredCells, hasInspectMessage, inDrawingOrder, indexCells, objectAt, objectSpotKey, useDurationOf, type CellIndex, type MapElement, type MapObject } from '../../shared/map-elements.ts';
import { flippedHitboxes, flipSwitches, mechanismStates, switchesAt } from '../../shared/mechanisms.ts';
import type { ObjectPose } from '../../shared/object-motion.ts';
import { tripPose } from '../../shared/vehicles.ts';
import { carriedAt, forcedSlide, isSurfable, planStep, PUSH_MS, type Mover, type MoveWorld } from '../../shared/movement.ts';
import type { WorldMapView } from '../../shared/world-map.ts';
import type { Move } from '../../shared/moves.ts';
import { INTERACTIONS } from '../../shared/tiles.ts';
import { createScene, type Scene } from '../common/element-renderer.ts';
import { $, el, scheduleFrame, shortcutLetter } from '../common/dom.ts';
import { icon } from '../common/icons.ts';
import type { IconName } from '../../shared/icons.ts';
import type { Toast } from '../common/toast.ts';
import type { AppearancePicker } from './appearance-picker.ts';
import type { BattleScreen } from './battle-screen.ts';
import { BattleField } from './battle-field.ts';
import type { ChatBox } from './chat-box.ts';
import type { CoopChip, InviteDialog, PlayerMenu } from './duel-ui.ts';
import type { HelpPanel } from './help-panel.ts';
import type { InspectPanel } from './inspect-panel.ts';
import type { BadgeCeremony } from './badge-ceremony.ts';
import type { LootReveal } from './loot-reveal.ts';
import type { MoveLearnDialog } from './move-learn-dialog.ts';
import type { DialogueBox } from './dialogue-box.ts';
import type { InteractionPrompt, PromptView } from './interaction-prompt.ts';
import type { QuestLog } from './quest-log.ts';
import type { BagPanel } from './bag-panel.ts';
import type { OptionsPanel } from './options-panel.ts';
import { Entity } from './entity.ts';
import { GameClock } from './game-clock.ts';
import type { EvolutionScene } from './evolution-scene.ts';
import { directionForKey, type DirectionInput } from './input.ts';
import type { PokedexPanel } from './pokedex-panel.ts';
import type { TrainerCardPanel } from './trainer-card-panel.ts';
import type { ShopPanel } from './shop-panel.ts';
import type { SoundBoard } from './sound.ts';
import { footstepAt } from '../../shared/footsteps.ts';
import type { Soundtrack } from './soundtrack.ts';
import type { StarterPicker } from './starter-picker.ts';
import type { StoragePanel } from './storage-panel.ts';
import type { MonsterSheet } from './monster-sheet.ts';
import type { TeamPanel } from './team-panel.ts';
import type { WorldMapPanel } from './world-map-panel.ts';
import type { WorldRenderer } from './world-renderer.ts';
import { creatureText, gameConfig, setGameConfig } from '../../shared/game-config.ts';
import { applyGameTexts } from './game-texts.ts';
import { ConnectionIndicator } from './connection-indicator.ts';

type GameSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

const REQUEST_TIMEOUT_MS = 5000;
/** Un pas de PNJ est animé un peu plus longtemps que son rythme, pour enchaîner sans pause (gigue réseau comprise). */
const NPC_STEP_OVERLAP = 1.15;
/** Délai avant de montrer le butin d'un coffre : le temps qu'il s'ouvre. */
const LOOT_DELAY_MS = 500;

interface WorldState {
  map: MapView;
  self: Entity;
  others: Map<number, Entity>;
  npcs: Map<number, { entity: Entity; view: NpcView }>;
  zoneProgress: ZoneProgressMap;
}

/** Ce que les zones à effets d'une carte changent aux pas : vitesse, glace et courant de chaque case. */
interface MapBuffs {
  speeds: Map<number, number>;
  slides: Set<number>;
  currents: Map<number, Direction>;
}

/** Transition de changement de carte : fermeture du cercle, noir, réouverture. */
/** Part de l'animation du Vol où le {monstre} rejoint le joueur, avant de s'élever avec lui. */
const FLY_GRAB = 0.3;
/** Hauteur et dérive latérale (en cases) atteintes par le joueur à la fin de la montée du Vol ; l'atterrissage fait le chemin inverse. */
const FLY_HEIGHT = 4;
const FLY_DRIFT = 3;
/** Atterrissage : attente (le cercle se rouvre), descente, puis départ du {monstre} (somme : `ABILITY_ANIMATION_MS.land`). */
const LAND_DELAY_MS = 500;
const LAND_DESCENT_MS = 800;
const LAND_LEAVE_MS = 400;
/** Demi-tour : le joueur se retourne d'abord sur place ; s'il garde la touche enfoncée, il part après ce délai. */
const TURN_MS = 130;
const TRANSITION_CLOSE_MS = 380;
const TRANSITION_HOLD_MS = 120;
const TRANSITION_OPEN_MS = 450;
/** Messages propres à la carte affichée (voir onMapEvent). */
type MapEvent = 'map:update' | 'player:joined' | 'player:left' | 'player:moved' | 'player:pet' | 'npcs:update' | 'obstacle:cleared' | 'object:pushed'
  | 'npc:guest' | 'npc:gone' | 'npc:moved' | 'drop:opened' | 'mapMonsters:update' | 'mapMonster:moved' | 'objects:update' | 'map:switches'
  | 'usable:update' | 'inspectable:update' | 'emote' | 'hop';
/** Téléporteur pris sans attendre le serveur : sans réponse au bout de ce délai, on revient sur la carte quittée. */
const PREDICTED_WARP_TIMEOUT_MS = 10_000;
/** Invite « Caresser … » face à son {monstre} suiveur : jouée sur place, sans passer par `interact`. */
const PET_PROMPT = 'pet';

/** Fenêtre ou scène de l'interface, fermée à l'arrêt du client. */
interface GamePanel {
  readonly isOpen: boolean;
  close(): void;
}

interface PanelEntry {
  panel: GamePanel;
  /** Ouverte, elle bloque les déplacements. */
  blocking: boolean;
  /** Fermée par Échap (et quand le serveur impose un choix). */
  closable: boolean;
}

const npcEntries = (npcs: NpcView[]) =>
  new Map(npcs.map((view) => [view.id, { entity: Entity.fromNpc(view), view }]));

/** Composants d'interface, créés une seule fois et partagés entre les connexions successives. */
export interface GameUi {
  renderer: WorldRenderer;
  battle: BattleScreen;
  evolution: EvolutionScene;
  team: TeamPanel;
  chat: ChatBox;
  toast: Toast;
  pokedex: PokedexPanel;
  shop: ShopPanel;
  achievements: AchievementsPanel;
  help: HelpPanel;
  worldMap: WorldMapPanel;
  inspect: InspectPanel;
  trainerCard: TrainerCardPanel;
  prompt: InteractionPrompt;
  dialogue: DialogueBox;
  badgeCeremony: BadgeCeremony;
  moveLearn: MoveLearnDialog;
  lootReveal: LootReveal;
  questLog: QuestLog;
  bag: BagPanel;
  options: OptionsPanel;
  playerMenu: PlayerMenu;
  invite: InviteDialog;
  coopInvite: InviteDialog;
  coop: CoopChip;
  starter: StarterPicker;
  storage: StoragePanel;
  sheet: MonsterSheet;
  appearance: AppearancePicker;
  sounds: SoundBoard;
  soundtrack: Soundtrack;
}

export interface GameClientHandlers {
  /** Le jeton est refusé, ou le compte s'est connecté ailleurs. */
  onSessionLost(message: string): void;
}

/**
 * Client de jeu : applique les messages du serveur à l'état local, prédit ses propres
 * déplacements (le serveur corrige si besoin) et pilote le rendu et l'interface.
 */
const TIME_HINTS: Record<'day' | 'night', { icon: IconName; label: string }> = {
  day: { icon: 'sun', label: 'le jour' },
  night: { icon: 'moon', label: 'la nuit' },
};
const WEATHER_HINTS: Record<'clear' | 'rain' | 'snow' | 'fog', { icon: IconName; label: string }> = {
  clear: { icon: 'sun', label: 'par temps clair' },
  rain: { icon: 'rain', label: 'sous la pluie' },
  snow: { icon: 'snow', label: 'sous la neige' },
  fog: { icon: 'fog', label: 'dans le brouillard' },
};

export class GameClient {
  private readonly socket: GameSocket;
  /** Mini chargement quand la connexion est coupée ou lente. */
  private readonly connection: ConnectionIndicator;
  private readonly handlers: GameClientHandlers;
  private readonly root = $('#game');
  /** Effets d'écran des scènes (fondu, tremblement, flash), par-dessus la carte. */
  private readonly screenFx = new ScreenEffects($('#game'), $('#world'));
  /** Indication « Échap : passer la scène », montrée pendant une scène. */
  private readonly skipHint = $('#game').querySelector<HTMLElement>(':scope > .scene-skip-hint')
    ?? $('#game').appendChild(Object.assign(document.createElement('div'), { className: 'scene-skip-hint', textContent: 'Échap : passer la scène', hidden: true }));
  private readonly ui: GameUi;
  /** Tous les éléments des cartes, reçus à la connexion puis à chaque modification dans l'administration. */
  private elements = new Map<number, MapElement>();
  /** Apparences des personnages, reçues à la connexion puis à chaque modification dans l'administration. */
  private characters = new Map<number, CharacterDesign>();
  /** Rochers poussés en train de glisser vers leur nouvelle case (l'objet de la carte y est déjà). */
  /** Capacité en cours d'animation (couper : l'obstacle tremble ; voler : le joueur s'élève). */
  private ability: { animation: AbilityAnimation; startedAt: number; departed?: boolean } | null = null;
  /** Atterrissage en cours : le {monstre} dépose le joueur sur la nouvelle carte. */
  private landing: { mount: FollowerView; startedAt: number } | null = null;
  /**
   * Voyage en véhicule : l'objet posé qui emmène le joueur, la case du joueur par rapport au coin de l'objet, et
   * `departed` une fois la nouvelle carte reçue (le trajet continue sous le cercle qui se referme).
   */
  private ride: { ride: VehicleRide; object: MapObject; offset: Position; startedAt: number; departed: boolean } | null = null;
  private pushes: { object: MapObject; from: Position; startedAt: number }[] = [];
  /** Pas de nouvelle poussée avant cet instant : une case à la fois (le serveur vérifie aussi). */
  private pushReadyAt = 0;
  /** Fin du demi-tour sur place en cours : pas de pas avant. */
  private turnUntil = 0;
  /** Maj enfoncée : le joueur court. */
  private running = false;
  /** Vitesse des joueurs et cases de glace, pour chaque carte reçue. */
  private readonly speeds = new WeakMap<MapView, MapBuffs>();
  /** Un dresseur vient défier le joueur : il attend sans pouvoir bouger. */
  private cutscene = false;
  /** Scène : personnage qui vient de parler (ou de montrer une émotion), que la caméra garde à l'écran. */
  private sceneFocus: number | null = null;
  /** Haut de la boîte de dialogue de la scène, retenu entre deux répliques : la caméra ne monte et ne descend pas à chaque fois. */
  private sceneBottom = innerHeight;
  /** Dresseur qui s'approche du joueur le temps de son dialogue (position purement visuelle). */
  private approacher: { npcId: number; timer: ReturnType<typeof setTimeout> | undefined } | null = null;
  /**
   * Changement de carte en cours : l'ancienne carte, affichée pendant la fermeture du cercle. `startedAt` nul : le
   * cercle attend la fin du combat (défaite : retour à la borne de soin une fois l'écran de combat refermé).
   */
  private transition: { leaving: WorldState; startedAt: number | null } | null = null;
  /** Téléporteurs de la carte pris sans attendre le serveur (anti-triche coupé, voir WarpPreviews). */
  private warpPreviews: WarpPreviews | null = null;
  /**
   * Téléporteur pris sans attendre le serveur : la carte quittée (on y revient s'il refuse le passage) et celle
   * d'arrivée (son map:change ne relance pas la transition).
   */
  private predictedWarp: { from: WorldState; toMap: number; timeout: ReturnType<typeof setTimeout> } | null = null;
  /** Arrivée prédite sur un téléporteur : un demi-tour sur cette case y ramène aussitôt s'il est à retour rapide. */
  private arrival: { mapId: number; x: number; y: number; back: Direction } | null = null;
  private scene: { map: MapView; scene: Scene } | null = null;
  /** Collisions pour les pas et état des mécanismes sur cette carte, avec ces interrupteurs allumés (voir mechanismsOf). */
  private mechanismCache: { map: MapView; objects: readonly MapObject[]; on: string; cells: CellIndex; states: Map<MapObject, boolean> } | null = null;
  private readonly input: DirectionInput;
  /** Détache les écouteurs globaux quand le client est arrêté. */
  private readonly listeners = new AbortController();
  private world: WorldState | null = null;
  /** Partenaire de coop (null : on joue seul) ; il se tient à côté dans l'arène des combats. */
  private partnerId: number | null = null;
  /** Messages (quêtes, succès…) reçus à la fin d'un combat : montrés de retour sur la carte, pour ne pas devancer le récit. */
  private afterBattle: (() => void)[] | null = null;
  private coins: number | null = null;
  private readonly clock = new GameClock($('#game-clock'));
  /** Arène des combats, posée sur la carte autour du joueur. */
  private readonly field = new BattleField($('#battle'), $<HTMLCanvasElement>('#world'));
  /** Toutes les fenêtres, dans l'ordre de fermeture (le combat d'abord : sa fin peut lancer évolution et badge). */
  private readonly panels: PanelEntry[];
  /** Raccourcis clavier, par lettre tapée (selon la disposition du clavier) : la touche ouvre la fenêtre, et la referme. */
  private readonly hotkeys: Partial<Record<string, GamePanel & { toggle(): void }>>;

  constructor(token: string, ui: GameUi, input: DirectionInput, handlers: GameClientHandlers) {
    this.ui = ui;
    const scene = (panel: GamePanel): PanelEntry => ({ panel, blocking: true, closable: false });
    const modal = (panel: GamePanel): PanelEntry => ({ panel, blocking: true, closable: true });
    this.panels = [
      scene(ui.battle), scene(ui.evolution), scene(ui.badgeCeremony), scene(ui.dialogue), scene(ui.starter), scene(ui.appearance),
      modal(ui.moveLearn), modal(ui.help), modal(ui.worldMap), modal(ui.inspect), modal(ui.trainerCard), modal(ui.sheet), modal(ui.storage), modal(ui.questLog),
      modal(ui.achievements), modal(ui.bag), modal(ui.options), modal(ui.pokedex), modal(ui.shop),
      { panel: ui.team, blocking: false, closable: true },
      { panel: ui.playerMenu, blocking: false, closable: true },
      { panel: ui.invite, blocking: false, closable: false },
    ];
    this.hotkeys = {
      p: ui.pokedex, u: ui.achievements, h: ui.help,
      m: ui.worldMap, o: ui.options, v: ui.options, b: ui.bag, j: ui.questLog, c: ui.trainerCard,
    };
    this.input = input;
    this.handlers = handlers;
    this.socket = io({ auth: { token }, transports: ['websocket'] });
    this.connection = new ConnectionIndicator(this.root, this.socket);
    this.bindSocket();
    this.bindKeyboard();
    this.bindPointer();
    scheduleFrame((now) => this.frame(now));
  }

  stop(): void {
    this.listeners.abort();
    this.connection.stop();
    this.socket.disconnect();
    this.input.clear();
    // Les fenêtres sont partagées avec la connexion suivante : aucune ne doit rester ouverte (dialogue compris).
    for (const { panel } of this.panels) panel.close();
    this.ui.soundtrack.stop();
    this.root.hidden = true;
  }

  /** Le joueur est de retour sur la carte (récit du combat, évolutions et badge passés) : les messages retenus s'affichent. */
  releaseBattleNotices(): void {
    const held = this.afterBattle ?? [];
    this.afterBattle = null;
    for (const show of held) show();
  }

  // ---------- Commandes envoyées par l'interface ----------

  sendBattleAction(action: BattleAction): void {
    this.socket.emit('battle:action', action);
  }

  sendTeamAction(action: TeamAction): void {
    this.socket.emit('team', action);
  }

  usePotion(item: ItemId, monsterId: number): void {
    this.socket.emit('item:use', { item, monsterId });
  }

  useFieldItem(item: ItemId): void {
    this.socket.emit('item:field', { item });
  }

  equip(item: ItemId, equipped: boolean): void {
    this.socket.emit('item:equip', { item, equipped });
  }

  buy(item: ItemId, quantity: number): void {
    this.socket.emit('shop:buy', { item, quantity });
  }

  setMonsterMoves(monsterId: number, moveIds: number[]): void {
    this.socket.emit('monster:moves', { monsterId, moveIds });
  }

  setFollower(monsterId: number | null): void {
    this.socket.emit('follower:set', { monsterId });
  }

  setCharacter(characterId: number | null, outfit: CharacterOutfit | null): void {
    this.socket.emit('character:set', { characterId, outfit });
  }

  pickStarter(speciesId: number): void {
    this.socket.emit('starter:pick', { speciesId });
  }

  challenge(playerId: number): void {
    this.socket.emit('duel:challenge', { playerId });
  }

  respondToDuel(fromId: number, accept: boolean): void {
    this.socket.emit('duel:respond', { fromId, accept });
  }

  inviteCoop(playerId: number): void {
    this.socket.emit('coop:invite', { playerId });
  }

  respondToCoop(fromId: number, accept: boolean): void {
    this.socket.emit('coop:respond', { fromId, accept });
  }

  leaveCoop(): void {
    this.socket.emit('coop:leave');
  }

  /** Bouton B (écran tactile) maintenu : le joueur court, comme avec Maj. */
  setRunning(running: boolean): void {
    this.running = running;
  }

  /** Utilise l'objet situé face au joueur (borne de soin, comptoir…). */
  interact(): void {
    if (!this.ui.prompt.available || this.overlayOpen) return;
    if (this.ui.prompt.key === PET_PROMPT) return this.petFollower();
    this.socket.emit('interact');
  }

  /** Caresse le {monstre} suiveur qu'on regarde : le cœur s'affiche tout de suite, les autres joueurs le voient ensuite. */
  private petFollower(): void {
    const self = this.world?.self;
    const now = performance.now();
    // Une caresse à la fois : on peut recommencer quand le cœur est à mi-course.
    if (!self || (self.petting(now) ?? 1) < 0.55) return;
    self.pet(now);
    this.ui.sounds.play('pet');
    this.socket.emit('follower:pet');
  }

  replyToDialogue(id: number, choice: number | null): void {
    this.returnApproacher();
    this.socket.emit('dialogue:reply', { id, choice });
  }

  /**
   * Un dresseur qui a repéré le joueur vient à sa rencontre, côté client seulement (sa vraie case, côté serveur
   * et dans `view`, ne change pas) ; `then` s'exécute quand il est collé au joueur.
   */
  private approachPlayer(npcId: number, then: () => void): void {
    const { world } = this;
    const npc = world?.npcs.get(npcId);
    if (!world || !npc) return then();
    this.returnApproacher();
    const { entity } = npc;
    const walkable = this.moveWorld(world);
    const player = { x: world.self.x, y: world.self.y };
    // Plus court chemin (pas simples seulement) vers une case voisine du joueur.
    const key = (p: Position) => p.y * world.map.width + p.x;
    const from = { x: entity.x, y: entity.y };
    const previous = new Map<number, Position | null>([[key(from), null]]);
    const queue: Position[] = [from];
    let goal: Position | null = null;
    for (let i = 0; i < queue.length && !goal; i++) {
      const cell = queue[i]!;
      if (Math.abs(cell.x - player.x) + Math.abs(cell.y - player.y) === 1) { goal = cell; break; }
      for (const dir of ['up', 'down', 'left', 'right'] as const) {
        const plan = planStep(walkable, { x: cell.x, y: cell.y, dir, surfing: false, skates: false }, dir);
        if (plan.kind !== 'move') continue;
        const { target } = plan;
        if (Math.abs(target.x - cell.x) + Math.abs(target.y - cell.y) !== 1 || previous.has(key(target))) continue;
        if (target.x === player.x && target.y === player.y) continue;
        previous.set(key(target), cell);
        queue.push(target);
      }
    }
    if (!goal) return then();
    const path: Position[] = [];
    for (let cell: Position | null = goal; cell && previous.get(key(cell)); cell = previous.get(key(cell)) ?? null) path.unshift(cell);
    const stepMs = WALK_STEP_MS;
    const state = { npcId, timer: undefined as ReturnType<typeof setTimeout> | undefined };
    this.approacher = state;
    const walk = (): void => {
      const next = path.shift();
      if (!next) {
        const dx = player.x - entity.x, dy = player.y - entity.y;
        entity.dir = Math.abs(dx) >= Math.abs(dy) ? (dx < 0 ? 'left' : 'right') : (dy < 0 ? 'up' : 'down');
        state.timer = undefined;
        return then();
      }
      entity.dir = next.x < entity.x ? 'left' : next.x > entity.x ? 'right' : next.y < entity.y ? 'up' : 'down';
      entity.moveTo(next, true, false, stepMs);
      state.timer = setTimeout(walk, stepMs);
    };
    walk();
  }

  /** Le dresseur venu au contact regagne (instantanément) sa case réelle, celle du serveur. */
  private returnApproacher(): void {
    const { approacher } = this;
    if (!approacher) return;
    this.approacher = null;
    clearTimeout(approacher.timer);
    const npc = this.world?.npcs.get(approacher.npcId);
    if (!npc) return;
    npc.entity.moveTo(npc.view, false);
    npc.entity.dir = npc.view.dir;
  }

  /** Sans joueur (null) : sa propre carte. */
  fetchTrainerCard(playerId: number | null): Promise<TrainerCardView | null> {
    return this.socket.timeout(REQUEST_TIMEOUT_MS).emitWithAck('trainer-card:get', { playerId });
  }

  fetchAchievements(): Promise<AchievementView[]> {
    return this.socket.timeout(REQUEST_TIMEOUT_MS).emitWithAck('achievements:get');
  }

  fetchQuests(): Promise<QuestLogEntry[]> {
    return this.socket.timeout(REQUEST_TIMEOUT_MS).emitWithAck('quests:get');
  }

  sendChat(text: string): void {
    this.socket.emit('chat', text);
  }

  fetchPokedex(): Promise<PokedexView> {
    return this.socket.timeout(REQUEST_TIMEOUT_MS).emitWithAck('pokedex:get');
  }

  inspectPlayer(playerId: number): Promise<InspectedPlayer | null> {
    return this.socket.timeout(REQUEST_TIMEOUT_MS).emitWithAck('player:inspect', { playerId });
  }

  fetchWorldMap(): Promise<WorldMapView> {
    return this.socket.timeout(REQUEST_TIMEOUT_MS).emitWithAck('worldmap:get');
  }

  /** Le Vol : s'envoler vers cette ville (le serveur vérifie les conditions, puis change de carte). */
  fly(mapId: number): void {
    this.socket.emit('fly', { mapId });
  }

  // ---------- Réseau ----------

  private bindSocket(): void {
    const { socket, ui } = this;
    socket.on('connect_error', (error) => {
      if (error.message === 'auth') this.handlers.onSessionLost('');
    });
    socket.on('kicked', (message) => this.handlers.onSessionLost(message));

    socket.on('init', ({ collection, inventory, elements, moves, characters, items, animations, sounds, types, admin, ...snapshot }) => {
      this.releaseBattleNotices();
      // Les catalogues de types et d'objets d'abord : sac, équipe et boutique les utilisent pour s'afficher.
      elementTypes.set(types);
      itemCatalog.set(items);
      animationCatalog.set(animations);
      ui.soundtrack.setCatalog(sounds);
      ui.battle.setPlayerName(snapshot.self.username);
      $('#btn-admin').hidden = !admin;
      this.root.hidden = false;
      this.setElements(elements);
      this.characters = new Map(characters.map((design) => [design.id, design]));
      this.setMoves(moves);
      ui.help.openOnFirstVisit();
      // Reconnexion : le serveur renvoie ce qui est en cours (combat, dialogue) ; le PNJ venu défier le joueur est reparti.
      ui.battle.close();
      this.cutscene = false;
      this.sceneFocus = null;
      this.skipHint.hidden = true;
      this.screenFx.apply('clear', 0);
      this.forgetPredictedWarp();
      this.warpPreviews = null;
      this.arrival = null;
      this.loadWorld(snapshot);
      this.applyCollection(collection);
      this.applyInventory(inventory);
      void ui.worldMap.preload();
    });
    socket.on('map:change', (snapshot) => {
      // Téléporteur déjà pris sans attendre : la carte affichée est la bonne, seul son contenu est mis à jour.
      const predicted = this.predictedWarp;
      this.forgetPredictedWarp();
      if (predicted && this.world && snapshot.map.id === predicted.toMap && this.world.map.id === predicted.toMap) return this.confirmWarp(this.world, snapshot);
      this.arrival = null;
      this.closeOverlays();
      this.input.clear();
      // Le nouvel état est appliqué tout de suite (les messages suivants le concernent) ; seul
      // l'affichage de l'ancienne carte est gardé le temps que le cercle se referme.
      const leaving = this.world;
      // Le Vol continue sous le cercle qui se referme : il s'arrête avec l'ancienne carte.
      if (this.ability?.animation.kind === 'fly') this.ability.departed = true;
      if (this.ride) this.ride.departed = true;
      this.loadWorld(snapshot);
      // Un nouveau lieu exploré : la carte du monde se redessine en arrière-plan.
      void ui.worldMap.preload();
      if (leaving && !this.root.hidden) {
        this.transition = { leaving: this.transition?.leaving ?? leaving, startedAt: ui.battle.isOpen ? null : performance.now() };
      }
    });
    this.onMapEvent('map:update', ({ map, position, zoneProgress, npcs }) => {
      if (!this.world) return;
      this.world.map = map;
      this.ui.soundtrack.setMap(map);
      this.world.zoneProgress = zoneProgress;
      this.world.npcs = npcEntries(npcs);
      this.world.self.moveTo(position, false);
      this.refreshHud();
    });
    socket.on('elements:update', (elements) => this.setElements(elements));
    socket.on('characters:update', (characters) => {
      this.characters = new Map(characters.map((design) => [design.id, design]));
    });
    socket.on('moves:update', (moves) => this.setMoves(moves));
    socket.on('types:update', (types) => elementTypes.set(types));
    // Le serveur renvoie ensuite le sac, qui redessine ce qui affiche des objets.
    socket.on('items:update', (items) => itemCatalog.set(items));
    socket.on('sounds:update', (sounds) => ui.soundtrack.setCatalog(sounds));
    // Nouveau nom des créatures : textes fixes mis à jour tout de suite, panneaux à leur prochain affichage.
    socket.on('config:update', (config) => {
      setGameConfig(config);
      applyGameTexts();
      ui.soundtrack.refresh();
    });
    socket.on('animations:update', (animations) => animationCatalog.set(animations));
    socket.on('zone:progress', (zoneProgress) => {
      if (!this.world) return;
      this.world.zoneProgress = zoneProgress;
      this.refreshHud();
    });

    this.onMapEvent('player:joined', (player) => this.world?.others.set(player.id, new Entity(player)));
    this.onMapEvent('player:left', ({ id }) => this.world?.others.delete(id));
    this.onMapEvent('player:moved', (player) => {
      // Le joueur lui-même reçoit ce message quand il change de monstre suiveur ou d'apparence.
      if (player.id === this.world?.self.id) {
        this.world.self.applyView(player);
        return;
      }
      const { world } = this;
      const entity = world?.others.get(player.id);
      if (!world || !entity) return void world?.others.set(player.id, new Entity(player));
      entity.applyView(player);
      entity.dir = player.dir;
      if (entity.x !== player.x || entity.y !== player.y) this.animateStep(world.map, entity, player, player.running);
    });
    socket.on('self:position', (position) => {
      // Le serveur n'a pas fait passer le joueur par le téléporteur pris sans attendre : il revient sur la carte quittée.
      if (this.predictedWarp) this.cancelPredictedWarp();
      this.correctSelf(position);
    });
    socket.on('warps:preview', (previews) => {
      this.warpPreviews = previews;
    });

    socket.on('online:count', (count) => { $('#online-count').textContent = `${count} en ligne`; });
    socket.on('notice', (message) => this.showAfterBattle(() => ui.toast.show(message)));
    socket.on('quest:text', (pages) => this.showAfterBattle(() => ui.dialogue.queueNote(pages)));
    socket.on('appearance:choose', (designs, current) => {
      this.closeOverlays();
      this.input.clear();
      ui.appearance.show(designs, current);
    });
    socket.on('starter:choose', (options) => {
      this.closeOverlays();
      ui.starter.show(options);
    });
    socket.on('collection', (collection) => this.applyCollection(collection));
    socket.on('inventory', (inventory) => this.applyInventory(inventory));
    socket.on('healed', () => {
      ui.prompt.playHeal();
      this.markFacedUsed();
    });
    // Les visiteurs (dresseur venu défier le joueur, PNJ qui s'en va, acteur de scène : identifiants négatifs) restent
    // jusqu'à leur propre départ.
    this.onMapEvent('npcs:update', (npcs) => {
      const { world } = this;
      if (!world) return;
      const visitors = [...world.npcs].filter(([id]) => id < 0);
      world.npcs = new Map([...npcEntries(npcs), ...visitors]);
    });
    // Capacité lancée : on joue son animation en attendant l'effet (le serveur bloque le joueur d'ici là).
    socket.on('ability:start', (animation) => {
      this.input.clear();
      if (animation.kind === 'land') {
        this.landing = { mount: animation.mount, startedAt: performance.now() };
        this.ui.sounds.play('whoosh');
        return;
      }
      this.ability = { animation, startedAt: performance.now() };
      this.ui.sounds.play(animation.kind === 'fly' ? 'whoosh' : 'slash');
    });
    // Départ en véhicule : le joueur, sur sa case d'embarquement, part avec l'objet (le serveur le bloque jusqu'à l'arrivée).
    socket.on('vehicle:ride', (ride) => {
      const { world } = this;
      const object = world?.map.objects.find((candidate) => candidate.elementId === ride.elementId && candidate.x === ride.x && candidate.y === ride.y);
      if (!world || !object) return;
      this.input.clear();
      world.self.moveTo(world.self, false);
      this.ride = { ride, object, offset: { x: world.self.x - object.x, y: world.self.y - object.y }, startedAt: performance.now(), departed: false };
    });
    // Obstacle dégagé (buisson coupé…) : il disparaît pour ce joueur, pour de bon.
    this.onMapEvent('obstacle:cleared', ({ elementId, x, y }) => {
      const { world } = this;
      if (!world) return;
      const index = world.map.objects.findIndex((object) => object.elementId === elementId && object.x === x && object.y === y);
      if (index >= 0) world.map = { ...world.map, objects: world.map.objects.filter((_, i) => i !== index) };
    });
    // Rocher poussé : il change de case pour ce joueur ; une nouvelle carte le remet à sa place.
    this.onMapEvent('object:pushed', ({ elementId, from, to }) => {
      const { world } = this;
      if (!world) return;
      const index = world.map.objects.findIndex((object) => object.elementId === elementId && object.x === from.x && object.y === from.y);
      const pushed = index >= 0 ? { ...world.map.objects[index]!, ...to } : null;
      if (!pushed) return;
      world.map = { ...world.map, objects: world.map.objects.map((object, i) => (i === index ? pushed : object)) };
      // Il glisse de son ancienne case à la nouvelle (les collisions, elles, sont déjà à jour).
      this.pushes.push({ object: pushed, from, startedAt: performance.now() });
      this.ui.sounds.play('hit');
    });
    // Le joueur monte sur son {monstre} : il avance sur la case d'eau en face (le serveur l'y a placé) ; ou il reprend pied.
    socket.on('self:surf', (mount) => {
      const { world } = this;
      if (!world) return;
      world.self.surf = mount;
      const water = facingCell(world.self, world.self.dir);
      if (mount && isSurfable(this.moveWorld(world), water)) this.animateStep(world.map, world.self, water, false);
    });
    socket.on('cutscene', (active, skippable) => {
      this.cutscene = active;
      this.sceneFocus = null;
      this.sceneBottom = innerHeight;
      this.skipHint.hidden = !(active && skippable);
      this.input.clear();
    });
    socket.on('scene:effect', ({ effect, ms }) => this.screenFx.apply(effect, ms));
    this.onMapEvent('npc:guest', (npc) => {
      this.world?.npcs.set(npc.id, { entity: Entity.fromNpc(npc), view: npc });
    });
    this.onMapEvent('npc:gone', ({ id }) => {
      this.world?.npcs.delete(id);
      if (id === this.sceneFocus) this.sceneFocus = null;
    });
    // Scène : le joueur fait un pas (ou se tourne) ; une émotion au-dessus d'un personnage.
    socket.on('self:walk', ({ x, y, dir, stepMs }) => {
      const self = this.world?.self;
      if (!self) return;
      self.dir = dir;
      if (self.x !== x || self.y !== y) self.moveTo({ x, y }, true, false, stepMs * NPC_STEP_OVERLAP);
    });
    this.onMapEvent('emote', ({ id, emote }) => {
      const { world } = this;
      if (!world) return;
      const entity = id === world.self.id ? world.self : (world.npcs.get(id)?.entity ?? world.others.get(id));
      entity?.showEmote(emote, performance.now());
      if (this.cutscene) this.sceneFocus = id;
    });
    this.onMapEvent('hop', ({ id }) => {
      const { world } = this;
      if (!world) return;
      const entity = id === world.self.id ? world.self : (world.npcs.get(id)?.entity ?? world.others.get(id));
      entity?.hopInPlace(performance.now());
      // L'acteur prend la parole : la caméra le garde à l'écran jusqu'au prochain qui parle.
      if (this.cutscene) this.sceneFocus = id;
    });
    this.onMapEvent('npc:moved', ({ id, x, y, dir, stepMs }) => {
      const npc = this.world?.npcs.get(id);
      if (!npc) return;
      const moved = npc.view.x !== x || npc.view.y !== y;
      npc.view = { ...npc.view, x, y, dir };
      npc.entity.dir = dir;
      // Le pas est animé sur toute sa durée, un peu plus même : le suivant arrive avant la fin, et la marche
      // s'enchaîne sans à-coups au lieu de s'arrêter entre deux cases.
      if (moved) npc.entity.moveTo({ x, y }, true, false, stepMs * NPC_STEP_OVERLAP);
    });
    socket.on('loot:found', (loot) => {
      // Le butin attend que le coffre soit ouvert.
      setTimeout(() => {
        ui.sounds.play('levelup');
        ui.lootReveal.show(loot);
      }, LOOT_DELAY_MS);
    });
    // Objet à drop fouillé : il joue son ouverture ; à usage unique, il disparaît pour ce joueur une fois l'animation finie.
    this.onMapEvent('drop:opened', ({ elementId, x, y, vanishes }) => {
      const { world } = this;
      if (!world) return;
      const object = world.map.objects.find((candidate) => candidate.elementId === elementId && candidate.x === x && candidate.y === y);
      if (!object) return;
      ui.renderer.markUsed(object);
      if (!vanishes) return;
      const element = this.sceneOf(world.map).elements.get(elementId);
      setTimeout(() => {
        if (this.world?.map.objects.includes(object)) this.world.map = { ...this.world.map, objects: this.world.map.objects.filter((candidate) => candidate !== object) };
      }, element ? useDurationOf(element) : 0);
    });
    this.onMapEvent('mapMonsters:update', (monsters) => {
      if (this.world) this.world.map.monsters = monsters;
    });
    this.onMapEvent('mapMonster:moved', ({ id, x, y, dir, stepMs }) => {
      const monster = this.world?.map.monsters.find((candidate) => candidate.id === id);
      if (!monster) return;
      if (monster.x !== x || monster.y !== y) ui.renderer.stepMonster(id, monster, stepMs * NPC_STEP_OVERLAP);
      Object.assign(monster, { x, y, dir });
    });
    this.onMapEvent('objects:update', (objects) => {
      if (this.world) this.world.map = { ...this.world.map, objects };
    });
    this.onMapEvent('map:switches', (switchesOn) => {
      if (this.world) this.world.map.switchesOn = switchesOn;
    });
    this.onMapEvent('usable:update', (usable) => {
      if (this.world) this.world.map.usable = usable;
    });
    this.onMapEvent('inspectable:update', (inspectable) => {
      if (this.world) this.world.map.inspectable = inspectable;
    });
    socket.on('dialogue', (dialogue) => {
      this.input.clear();
      if (dialogue.approach === undefined) return ui.dialogue.show(dialogue);
      this.approachPlayer(dialogue.approach, () => ui.dialogue.show(dialogue));
    });
    socket.on('dialogue:close', () => ui.dialogue.dismiss());
    socket.on('shop:open', (forSale) => {
      this.input.clear();
      this.markFacedUsed();
      ui.shop.show(forSale);
    });
    socket.on('storage:open', () => {
      this.input.clear();
      this.markFacedUsed();
      ui.team.close();
      ui.storage.open();
    });
    socket.on('duel:invite', (invite) => {
      ui.invite.show(invite);
      ui.sounds.play('duel');
    });
    socket.on('coop:invite', (invite) => {
      ui.coopInvite.show(invite);
      ui.sounds.play('select');
    });
    socket.on('coop:state', (state) => {
      this.partnerId = state.partner?.id ?? null;
      ui.coop.show(state);
    });
    this.onMapEvent('player:pet', ({ id }) => this.world?.others.get(id)?.pet(performance.now()));
    socket.on('chat', (message) => {
      ui.chat.append(message);
      const world = this.world;
      const speaker = world && (message.id === world.self.id ? world.self : world.others.get(message.id));
      speaker?.say(message.text);
    });

    socket.on('battle:start', ({ position, ...snapshot }) => {
      this.releaseBattleNotices();
      this.input.clear();
      this.closeOverlays();
      ui.invite.close();
      this.correctSelf(position);
      const world = this.world;
      if (world) {
        const npcs = [...world.npcs.values()].map(({ entity }) => entity);
        // En coop, le partenaire est dans le combat : les monstres ne doivent pas le cacher non plus.
        const partner = this.partnerId === null ? undefined : world.others.get(this.partnerId);
        if (partner) npcs.push(partner);
        this.field.begin(world.self, npcs, this.rivalOf(world, snapshot), world.map, performance.now(), { allies: snapshot.allies.length, foes: snapshot.foes.length });
      }
      ui.soundtrack.startBattle(snapshot.mode);
      ui.battle.open(snapshot);
    });
    socket.on('battle:update', (snapshot) => ui.battle.update(snapshot));
    socket.on('battle:end', (end) => {
      // Le serveur envoie juste après les messages de fin de combat : ils attendent que le joueur ferme l'écran.
      if (ui.battle.isOpen) this.afterBattle ??= [];
      ui.battle.end(end);
      this.applyCollection(end.collection);
    });
  }

  /** Affiche tout de suite, ou après le combat qui vient de se terminer. */
  private showAfterBattle(show: () => void): void {
    if (this.afterBattle) this.afterBattle.push(show);
    else show();
  }

  /**
   * Dresseur adverse à placer en face du joueur : le PNJ de ce nom le plus proche (combat de dresseur), ou le joueur
   * de ce nom (duel) ; null contre un monstre sauvage, ou s'il n'est pas sur la carte.
   */
  private rivalOf(world: WorldState, { mode, opponent }: BattleSnapshot): Entity | null {
    if (mode === 'wild' || !opponent) return null;
    const candidates = mode === 'duel' ? [...world.others.values()] : [...world.npcs.values()].map(({ entity }) => entity);
    const { x, y } = world.self;
    const distance = (entity: Entity) => Math.abs(entity.x - x) + Math.abs(entity.y - y);
    return candidates.filter((entity) => entity.username === opponent).sort((a, b) => distance(a) - distance(b))[0] ?? null;
  }

  /** Catalogue des attaques : menus de combat et fiches des monstres. */
  private setMoves(moves: Move[]): void {
    const catalog = new Map(moves.map((move) => [move.id, move]));
    this.ui.battle.setMoveCatalog(catalog);
    this.ui.sheet.setMoveCatalog(catalog);
    this.ui.moveLearn.setMoveCatalog(catalog);
    this.ui.bag.setMoveCatalog(catalog);
  }

  private setElements(elements: MapElement[]): void {
    this.elements = new Map(elements.map((element) => [element.id, element]));
    this.scene = null;
    this.mechanismCache = null;
  }

  /** La carte et ses éléments, prêts à dessiner (recalculés quand l'une ou les autres changent). */
  private buffsOf(map: MapView): MapBuffs {
    let buffs = this.speeds.get(map);
    if (!buffs) {
      const { width, buffZones } = map;
      this.speeds.set(map, buffs = { speeds: speedCells(width, buffZones), slides: slideCells(width, buffZones), currents: currentCells(width, buffZones) });
    }
    return buffs;
  }

  /** `entity` est-il emporté sur cette case (glace, sauf s'il porte des patins, ou courant) ? */
  private slidesOn(map: MapView, entity: Entity, position: Position): boolean {
    const { slides, currents } = this.buffsOf(map);
    return carriedAt({ width: map.width, height: map.height, slides, currents }, { skates: entity.abilities.includes('ice-skates') }, position);
  }

  /**
   * Anime le pas d'un personnage de `entity` vers `target` : à l'allure et à la vitesse de la zone où il arrive, ou en
   * glissant (en tournant sur lui-même) s'il part de la glace ou y arrive.
   */
  private animateStep(map: MapView, entity: Entity, target: { x: number; y: number }, running: boolean): void {
    entity.sliding = this.slidesOn(map, entity, entity) || this.slidesOn(map, entity, target);
    const stepMs = entity.sliding ? SLIDE_STEP_MS : (running ? RUN_STEP_MS : WALK_STEP_MS) / (this.buffsOf(map).speeds.get(target.y * map.width + target.x) ?? 1);
    entity.moveTo(target, true, running, stepMs);
  }

  /**
   * Mécanismes de la carte avec les interrupteurs allumés : état de chaque objet à mécanisme, et collisions pour les pas
   * (hitbox inversées par les mécanismes actifs).
   */
  private mechanismsOf(map: MapView): { cells: CellIndex; states: Map<MapObject, boolean> } {
    const on = map.switchesOn.join('\n');
    const cache = this.mechanismCache;
    if (cache?.map !== map || cache.objects !== map.objects || cache.on !== on) {
      const switchesOn = new Set(map.switchesOn);
      const flipped = flippedHitboxes(map.objects, switchesOn);
      this.mechanismCache = {
        map, objects: map.objects, on, states: mechanismStates(map.objects, switchesOn),
        cells: flipped.size ? indexCells(map, this.elements, flipped) : this.sceneOf(map).cells,
      };
    }
    return this.mechanismCache!;
  }

  private sceneOf(map: MapView): Scene {
    if (this.scene?.map !== map) this.scene = { map, scene: createScene(map, this.elements) };
    return this.scene.scene;
  }

  private loadWorld({ map, self, players, npcs, zoneProgress }: WorldSnapshot): void {
    this.world = {
      map, self: new Entity(self), others: new Map(players.map((p) => [p.id, new Entity(p)])), npcs: npcEntries(npcs), zoneProgress,
    };
    this.ui.soundtrack.setMap(map);
    this.refreshHud();
  }

  private applyCollection(collection: OwnedMonsterView[]): void {
    this.ui.team.render(collection);
    this.ui.storage.render(collection);
    this.ui.sheet.setCollection(collection);
    this.ui.moveLearn.setCollection(collection);
    this.ui.bag.setCollection(collection);
  }

  private applyInventory(inventory: Inventory): void {
    if (this.coins !== null && inventory.coins > this.coins) this.ui.sounds.play('coin');
    this.coins = inventory.coins;
    $('#coins b').textContent = String(inventory.coins);
    this.ui.battle.setInventory(inventory);
    this.ui.team.setInventory(inventory);
    this.ui.shop.setInventory(inventory);
    this.ui.bag.setInventory(inventory);
    // Ses propres équipements (le serveur ne renvoie ses capacités qu'aux autres joueurs de la carte).
    if (this.world) Object.assign(this.world.self, { abilities: [...abilitiesOf(inventory.equipped, itemCatalog)], light: equippedLight(inventory.equipped, itemCatalog) });
  }

  private correctSelf(position: Position): void {
    const self = this.world?.self;
    if (!self || (self.x === position.x && self.y === position.y)) return;
    self.moveTo(position, false);
    this.refreshHud();
  }

  // ---------- Entrées ----------

  /** Une fenêtre ouverte bloque les déplacements. */
  private get panelOpen(): boolean {
    return this.panels.some(({ panel, blocking }) => blocking && panel.isOpen);
  }

  /** Une fenêtre ou une cinématique bloque les déplacements. */
  private get overlayOpen(): boolean {
    return this.cutscene || this.panelOpen;
  }

  private closeOverlays(): void {
    for (const { panel, closable } of this.panels) if (closable) panel.close();
  }

  private bindKeyboard(): void {
    const options = { signal: this.listeners.signal };
    const { ui } = this;
    window.addEventListener('keydown', (event) => {
      if (this.root.hidden || ui.chat.isTyping) return;
      // Échap pendant une scène : elle est passée (jusqu'au prochain combat).
      if (event.key === 'Escape' && this.cutscene && !ui.battle.isOpen) {
        event.preventDefault();
        this.socket.emit('scene:skip');
        return;
      }
      if (ui.battle.isOpen) {
        ui.battle.handleKey(event);
        return;
      }
      if (ui.appearance.isOpen) {
        ui.appearance.handleKey(event);
        return;
      }
      if (ui.starter.isOpen) {
        ui.starter.handleKey(event);
        return;
      }
      if (ui.evolution.isOpen || ui.badgeCeremony.isOpen) return;
      if (ui.dialogue.isOpen) {
        ui.dialogue.handleKey(event);
        return;
      }
      if (event.key === 'Escape') {
        this.closeOverlays();
        return;
      }
      if (ui.sheet.isOpen && ui.sheet.handleKey(event)) return;
      const letter = shortcutLetter(event);
      const hotkey = letter === null ? undefined : this.hotkeys[letter];
      // Fenêtre ouverte : seule sa propre touche la referme.
      if (this.panelOpen) {
        if (hotkey?.isOpen) hotkey.close();
        return;
      }
      if (event.key === 'Shift') this.running = true;
      const dir = directionForKey(event.code);
      if (dir) {
        event.preventDefault();
        ui.playerMenu.close();
        this.input.press(dir, event.repeat);
      } else if (event.code === 'Space') {
        event.preventDefault();
        this.interact();
      } else if (event.key === 'Enter') {
        event.preventDefault();
        ui.chat.focus();
      } else if (letter === 'e' || letter === 'i') {
        ui.team.toggle();
      } else if (hotkey) {
        this.input.clear();
        hotkey.toggle();
      }
    }, options);
    window.addEventListener('keyup', (event) => {
      if (event.key === 'Shift') this.running = false;
      const dir = directionForKey(event.code);
      if (dir) this.input.release(dir);
    }, options);
    window.addEventListener('blur', () => {
      this.input.clear();
      this.running = false;
    }, options);
  }

  /** Un clic sur un autre dresseur ouvre son menu (défi en duel). */
  private bindPointer(): void {
    $('#world').addEventListener('click', (event) => {
      const world = this.world;
      if (!world || this.overlayOpen) return;
      const { x, y } = this.ui.renderer.cellAt(event.clientX, event.clientY);
      const target = [...world.others.values()].find((other) => other.x === x && (other.y === y || other.y === y + 1));
      if (target) this.ui.playerMenu.open(target, event.clientX, event.clientY);
      else this.ui.playerMenu.close();
    }, { signal: this.listeners.signal });
  }

  /** La carte telle que le joueur la voit, pour les règles de déplacement (les mêmes que le serveur : shared/movement.ts). */
  private moveWorld({ map, npcs }: WorldState): MoveWorld {
    const same = (a: Position) => (b: Position) => a.x === b.x && a.y === b.y;
    return {
      width: map.width, height: map.height, cells: this.mechanismsOf(map).cells, slides: this.buffsOf(map).slides, currents: this.buffsOf(map).currents, objects: map.objects, elements: this.elements,
      occupied: (position) => [...npcs.values()].some(({ view }) => same(position)(view)) || map.monsters.some(same(position)),
    };
  }

  private moverOf(self: Entity): Mover {
    return { x: self.x, y: self.y, dir: self.dir, surfing: self.surf !== null, skates: self.abilities.includes('ice-skates') };
  }

  /** Sur la glace, le joueur glisse tout droit tant que la case devant est libre : c'est ce pas-là, imposé. */
  private slideDirection(world: WorldState): Direction | null {
    return forcedSlide(this.moveWorld(world), this.moverOf(world.self));
  }

  /** Avance d'une case (prédiction locale) ; contre un obstacle, se contente de se tourner. */
  /** Demi-tour sur place : le joueur fait face à `dir` sans avancer. */
  private turn(dir: Direction, now: number): void {
    const world = this.world;
    if (!world || !this.socket.connected) return;
    world.self.dir = dir;
    this.turnUntil = now + TURN_MS;
    this.socket.emit('move', { dir, turn: true });
    // Demi-tour sur la case d'arrivée d'un téléporteur à retour rapide : il ramène aussitôt par lui.
    const { arrival } = this;
    if (arrival?.mapId !== world.map.id || arrival.back !== dir || world.self.x !== arrival.x || world.self.y !== arrival.y) return;
    const found = this.previewAt(world, world.self);
    if (found?.warp.quickReturn) this.enterPredicted(world, found, dir);
  }

  // ---------- Téléporteurs pris sans attendre (anti-triche coupé, voir WarpPreviews) ----------

  /**
   * Message propre à la carte affichée : ignoré pendant un passage pris sans attendre (il vient de la carte quittée ;
   * le map:change qui confirme le passage renvoie tout le contenu de la nouvelle).
   */
  private onMapEvent<E extends MapEvent>(event: E, handler: ServerToClientEvents[E]): void {
    const listener = (...args: unknown[]) => {
      if (!this.predictedWarp) (handler as (...received: unknown[]) => void)(...args);
    };
    this.socket.on(event, listener as never);
  }

  /** Téléporteur connu de cette case de la carte, et la case d'arrivée ; rien si l'on doit attendre le serveur. */
  private previewAt(world: WorldState, { x, y }: Position): { warp: WarpPreview; arrival: Position } | undefined {
    const previews = this.warpPreviews;
    if (gameConfig().antiCheat || this.predictedWarp || previews?.mapId !== world.map.id) return undefined;
    for (const warp of previews.warps) {
      const cell = warp.cells.find((candidate) => candidate.x === x && candidate.y === y);
      if (cell && previews.destinations[warp.toMap]) return { warp, arrival: cell.arrival };
    }
    return undefined;
  }

  /** Pas sur un téléporteur connu (pas un pas de côté d'une case à l'autre du même) : la carte suivante, tout de suite. */
  private predictWarp(world: WorldState, from: Position, target: Position, dir: Direction): void {
    const found = this.previewAt(world, target);
    if (found && this.previewAt(world, from)?.warp !== found.warp) this.enterPredicted(world, found, dir);
  }

  /**
   * Montre la carte d'arrivée, comme le ferait map:change (cercle qui se referme sur l'ancienne carte) ; le serveur
   * confirme ensuite (map:change), ou ramène le joueur à sa place (self:position).
   */
  private enterPredicted(world: WorldState, { warp, arrival }: { warp: WarpPreview; arrival: Position }, dir: Direction): void {
    const destination = this.warpPreviews?.destinations[warp.toMap];
    if (!destination) return;
    const { self } = world;
    const view: PlayerView = {
      id: self.id, username: self.username, color: self.color, dir, characterId: self.characterId, outfit: self.outfit, running: self.running,
      follower: self.follower, abilities: self.abilities, light: self.light, surf: self.surf, ...arrival,
    };
    this.closeOverlays();
    this.input.clear();
    this.predictedWarp = { from: world, toMap: warp.toMap, timeout: setTimeout(() => this.cancelPredictedWarp(), PREDICTED_WARP_TIMEOUT_MS) };
    this.loadWorld({ ...destination, self: view });
    this.arrival = { mapId: warp.toMap, ...arrival, back: oppositeDirection(dir) };
    if (!this.root.hidden) this.transition = { leaving: this.transition?.leaving ?? world, startedAt: this.ui.battle.isOpen ? null : performance.now() };
  }

  /** Le serveur confirme le passage : la carte reprend son contenu à jour ; le joueur, lui, garde ses pas déjà faits. */
  private confirmWarp(world: WorldState, snapshot: WorldSnapshot): void {
    const { self } = world;
    const { arrival } = this;
    self.applyView(snapshot.self);
    // Pas encore reparti, mais arrivé ailleurs que prévu : la case du serveur fait foi.
    if (arrival && self.x === arrival.x && self.y === arrival.y && (snapshot.self.x !== self.x || snapshot.self.y !== self.y)) self.moveTo(snapshot.self, false);
    Object.assign(world, { map: snapshot.map, others: new Map(snapshot.players.map((p) => [p.id, new Entity(p)])), npcs: npcEntries(snapshot.npcs), zoneProgress: snapshot.zoneProgress });
    this.ui.soundtrack.setMap(snapshot.map);
    this.refreshHud();
    void this.ui.worldMap.preload();
  }

  /** Le serveur n'a pas fait passer le joueur (ou ne répond pas) : retour sur la carte quittée. */
  private cancelPredictedWarp(): void {
    const predicted = this.predictedWarp;
    if (!predicted) return;
    this.forgetPredictedWarp();
    this.arrival = null;
    this.transition = null;
    this.world = predicted.from;
    this.ui.soundtrack.setMap(predicted.from.map);
    this.refreshHud();
  }

  private forgetPredictedWarp(): void {
    if (this.predictedWarp) clearTimeout(this.predictedWarp.timeout);
    this.predictedWarp = null;
  }

  private step(dir: Direction): void {
    const world = this.world;
    // Connexion coupée : pas de pas prédits que le serveur recevrait tous d'un coup au retour (il renvoie la position).
    if (!world || !this.socket.connected) return;
    const { self, map } = world;
    // Bordure à sauter, surf, glace, rocher : le serveur fait le même calcul.
    const plan = planStep(this.moveWorld(world), this.moverOf(self), dir);
    if (plan.kind === 'locked') return;
    const turned = self.dir !== dir;
    self.dir = dir;

    if (plan.kind === 'move') {
      const from = { x: self.x, y: self.y };
      // Le demi-tour qui ramène par le téléporteur d'arrivée ne vaut plus une fois reparti (comme côté serveur).
      this.arrival = null;
      // Le surfeur reprend pied en sortant de l'eau.
      if (plan.dismount) self.surf = null;
      // Interrupteur sous ses pieds : il bascule tout de suite (le serveur renvoie ensuite ceux qui sont allumés, qui font foi).
      const flipped = switchesAt(map.objects, this.elements, plan.target, 'step');
      if (flipped.length) {
        const on = new Set(map.switchesOn);
        flipSwitches(on, flipped);
        map.switchesOn = [...on];
      }
      this.animateStep(map, self, plan.target, this.running);
      // Bruit de pas, sauf sur l'eau (surf) ou en glissant.
      const footstep = self.surf === null && !plan.sliding ? footstepAt(map, this.elements, plan.target) : null;
      if (footstep) this.ui.sounds.footstep(footstep);
      this.socket.emit('move', { dir, run: this.running, ...plan.target });
      this.predictWarp(world, from, plan.target, dir);
      this.refreshHud();
    } else if (plan.kind === 'push' && performance.now() >= this.pushReadyAt) {
      // Contre un rocher, un pas le pousse d'une case (le serveur vérifie et renvoie sa nouvelle place) ; le suivant
      // attend qu'il ait fini de glisser.
      this.pushReadyAt = performance.now() + PUSH_MS;
      this.socket.emit('move', { dir });
    } else if (turned) {
      this.socket.emit('move', { dir });
    }
  }

  // ---------- Boucle ----------

  private frame(now: number): void {
    if (this.listeners.signal.aborted) return;
    scheduleFrame((next) => this.frame(next));
    const world = this.world;
    if (!world || this.root.hidden) return;
    // Texte de quête en attente : seulement quand rien d'autre n'occupe l'écran (combat, dialogue, fenêtre, scène).
    if (!this.overlayOpen) this.ui.dialogue.showNote();
    // Arrêt sur image d'un coup : tout le monde se fige autour de l'arène.
    if (this.ui.battle.frozen) return;
    this.clock.update(Date.now());
    if (!this.ui.battle.isOpen) this.field.end(now);
    this.field.setColors(this.ui.battle.slotColors());
    const battle = this.field.scene(now);

    const transition = this.transitionAt(now);
    world.self.update(now);
    // Pendant la fermeture du cercle, on voit encore l'ancienne carte (et le joueur qui s'élève toujours).
    const shown = transition?.closing && this.transition ? this.transition.leaving : world;
    const ability = this.abilityAt(now, transition?.closing === true);
    const landing = this.landingAt(now);
    // Vol : le {monstre} arrive sous le joueur (premier tiers), puis ils s'élèvent ensemble, en diagonale.
    let lift: { mount: FollowerView; arrival: number; leave: number } | undefined;
    if (ability?.animation.kind === 'fly') {
      const rise = Math.max(0, (ability.progress - FLY_GRAB) / (1 - FLY_GRAB));
      shown.self.hop = rise * rise * FLY_HEIGHT;
      shown.self.drift = rise * rise * FLY_DRIFT;
      lift = { mount: ability.animation.mount, arrival: Math.min(1, ability.progress / FLY_GRAB), leave: 0 };
    } else if (landing) {
      // Il descend en douceur, puis le {monstre} s'éloigne.
      const u = Math.min(1, Math.max(0, (landing.elapsed - LAND_DELAY_MS) / LAND_DESCENT_MS)), left = 1 - u;
      world.self.hop = left * left * FLY_HEIGHT;
      world.self.drift = -left * left * FLY_DRIFT;
      lift = { mount: landing.mount, arrival: 1, leave: Math.min(1, Math.max(0, (landing.elapsed - LAND_DELAY_MS - LAND_DESCENT_MS) / LAND_LEAVE_MS)) };
    }
    if (!ability && !landing && world.self.drift !== 0) Object.assign(world.self, { hop: 0, drift: 0 });
    const ride = this.rideAt(now, transition?.closing === true);
    // En voyage, le joueur est dessiné sur le véhicule, là où l'a emmené le trajet (la caméra le suit).
    if (ride) Object.assign(shown.self, { renderX: shown.self.x + ride.pose.x - ride.object.x, renderY: shown.self.y + ride.pose.y - ride.object.y });
    const focus = battle || transition ? null : this.focusOf(shown);
    if (!ability && !landing && !ride && !transition && !this.overlayOpen && !this.ui.chat.isTyping && !world.self.isMoving && now >= this.turnUntil) {
      const slide = this.slideDirection(world);
      const dir = slide ?? this.input.take();
      // Vers l'arrière, le joueur se retourne d'abord (pas sur la glace, où il glisse sans choisir).
      if (dir && !slide && dir === oppositeDirection(world.self.dir)) this.turn(dir, now);
      else if (dir) this.step(dir);
    }
    this.ui.prompt.show(transition || ability || landing || ride || this.overlayOpen || world.self.isMoving ? null : this.facedInteraction(world));
    // Fermeture : on voit encore l'ancienne carte (le joueur finit d'entrer dans le téléporteur).
    this.ui.renderer.render({
      map: shown.map, elements: this.sceneOf(shown.map), self: shown.self, others: shown.others.values(),
      npcs: [...shown.npcs.values()].map(({ entity, view }) => ({ entity, marker: view.marker })), characters: this.characters,
      pushed: this.pushedAt(now, shown.map, ability), mechanisms: this.mechanismsOf(shown.map).states,
      ...(lift ? { lift } : {}),
      ...(ride ? { ride } : {}),
      ...(transition ? { transition } : {}),
      ...(battle ? { battle } : {}),
      ...(focus ? { focus } : {}),
    }, now);
    this.field.place(this.ui.renderer, now, battle);
  }

  /**
   * Scène : le personnage qui parle, que la caméra garde à l'écran au-dessus de la boîte de dialogue (null : pas de
   * scène, ou personne n'a encore parlé).
   */
  private focusOf(world: WorldState): { entity: Entity; bottom: number } | null {
    const id = this.sceneFocus;
    if (id === null || !this.cutscene) return null;
    const entity = id === world.self.id ? world.self : (world.npcs.get(id)?.entity ?? world.others.get(id));
    if (this.ui.dialogue.isOpen) this.sceneBottom = this.ui.dialogue.top;
    return entity ? { entity, bottom: this.sceneBottom } : null;
  }

  /** Rochers en train de glisser, et où les dessiner (oubliés une fois arrivés ou la carte quittée). */
  private pushedAt(now: number, map: MapView, ability: { animation: AbilityAnimation; progress: number } | null): ReadonlyMap<MapObject, Position> | undefined {
    const shaken = ability?.animation.kind === 'clear' ? this.shakenObject(map, ability.animation, ability.progress) : null;
    this.pushes = this.pushes.filter(({ object, startedAt }) => now - startedAt < PUSH_MS && map.objects.includes(object));
    if (!this.pushes.length) return shaken ? new Map([shaken]) : undefined;
    return new Map([...(shaken ? [shaken] : []), ...this.pushes.map(({ object, from, startedAt }) => {
      const t = Math.min(1, (now - startedAt) / PUSH_MS);
      const eased = t * (2 - t);
      return [object, { x: from.x + (object.x - from.x) * eased, y: from.y + (object.y - from.y) * eased }] as [MapObject, Position];
    })]);
  }

  /** Animation de capacité en cours (null une fois terminée) et sa progression (0 → 1). */
  private abilityAt(now: number, closing: boolean): { animation: AbilityAnimation; progress: number } | null {
    if (!this.ability) return null;
    const { animation, startedAt, departed } = this.ability;
    const progress = (now - startedAt) / animation.ms;
    // Le Vol ne s'arrête pas à la fin de son temps : il continue de s'élever jusqu'à la fin du cercle qui se ferme.
    if (animation.kind === 'fly' ? (progress < 1 || !departed || closing) && now - startedAt < animation.ms + 4000 : progress < 1) return { animation, progress };
    this.ability = null;
    return null;
  }

  /**
   * Voyage en cours (null une fois terminé) : où est le véhicule et depuis combien de temps il est parti. Les calques
   * « au départ » se jouent d'abord (le véhicule attend), puis il suit son chemin ; le voyage dure jusqu'à la fin du
   * cercle qui se referme sur la nouvelle carte.
   */
  private rideAt(now: number, closing: boolean): { object: MapObject; pose: ObjectPose; departed: number; hidden: boolean } | null {
    if (!this.ride) return null;
    const { ride, object, startedAt, departed } = this.ride;
    const elapsed = now - startedAt;
    if ((departed && !closing) || elapsed > ride.ms + 4000) {
      this.ride = null;
      // Voyage interrompu sans changer de carte : le joueur retrouve sa case.
      if (!departed && this.world) this.world.self.moveTo(this.world.self, false);
      return null;
    }
    const still = { x: object.x, y: object.y, angle: 0, flipX: false, flipY: false, dir: object.motion?.facing ?? 'down' };
    const moving = elapsed - ride.departMs;
    const pose = moving > 0 && object.motion?.points.length ? tripPose(object, object.motion, moving) : still;
    // Un véhicule qui cache le joueur l'efface dès qu'il se met en route (après les calques « au départ »).
    const hidden = moving > 0 && this.elements.get(object.elementId)?.hidesRider === true;
    return { object, pose, departed: elapsed, hidden };
  }

  /** Atterrissage en cours (null une fois terminé) et le temps écoulé depuis son début. */
  private landingAt(now: number): { mount: FollowerView; elapsed: number } | null {
    if (!this.landing) return null;
    const elapsed = now - this.landing.startedAt;
    if (elapsed < LAND_DELAY_MS + LAND_DESCENT_MS + LAND_LEAVE_MS) return { mount: this.landing.mount, elapsed };
    this.landing = null;
    return null;
  }

  /** L'obstacle qu'on coupe tremble de plus en plus vite, puis s'écrase avant de disparaître. */
  private shakenObject(map: MapView, { elementId, x, y }: { elementId: number; x: number; y: number }, progress: number): [MapObject, Position] | null {
    const object = map.objects.find((candidate) => candidate.elementId === elementId && candidate.x === x && candidate.y === y);
    if (!object) return null;
    const shake = Math.sin(progress * progress * 60) * 0.08 * progress;
    return [object, { x: object.x + shake, y: object.y + progress * progress * 0.15 }];
  }

  /** Où en est la transition de changement de carte (null une fois terminée). */
  private transitionAt(now: number): { closing: boolean; progress: number } | null {
    if (!this.transition) return null;
    if (this.transition.startedAt === null) {
      if (this.ui.battle.isOpen) return { closing: true, progress: 0 };
      this.transition.startedAt = now;
    }
    const elapsed = now - this.transition.startedAt;
    if (elapsed < TRANSITION_CLOSE_MS) return { closing: true, progress: elapsed / TRANSITION_CLOSE_MS };
    const opening = elapsed - TRANSITION_CLOSE_MS - TRANSITION_HOLD_MS;
    if (opening < 0) return { closing: true, progress: 1 };
    if (opening < TRANSITION_OPEN_MS) return { closing: false, progress: opening / TRANSITION_OPEN_MS };
    this.transition = null;
    return null;
  }

  /** Le joueur vient de se servir de l'objet en face de lui (borne de soin, PC, caisse, coffre) : il joue son animation d'utilisation. */
  private markFacedUsed(): void {
    const { world } = this;
    if (!world) return;
    const { x, y } = facingCell(world.self, world.self.dir);
    const elements = this.sceneOf(world.map).elements;
    const object = world.map.objects.find((candidate) => {
      const element = elements.get(candidate.elementId);
      return element?.interaction && x >= candidate.x && x < candidate.x + element.width && y >= candidate.y && y < candidate.y + element.height;
    });
    if (object) this.ui.renderer.markUsed(object);
  }

  /** Ce que la touche d'action ferait : parler au PNJ en face, ou utiliser l'objet en face. */
  private facedInteraction({ map, self, npcs }: WorldState): PromptView | null {
    const { x, y } = facingCell(self, self.dir);
    // Hors de la carte : rien, pas même un PNJ sorti du champ sur son chemin.
    if (x < 0 || y < 0 || x >= map.width || y >= map.height) return null;
    const npc = [...npcs.values()].find(({ view }) => view.x === x && view.y === y)?.view;
    if (npc) return { key: `npc:${npc.id}`, icon: npc.marker === 'trainer' ? 'duel' : 'chat', label: `Parler à ${npc.name}` };
    const monster = map.monsters.find((candidate) => candidate.x === x && candidate.y === y);
    if (monster?.action === 'battle') return { key: `monster:${monster.id}`, icon: 'duel', label: `Affronter ${monster.name}` };
    if (monster?.action === 'talk') return { key: `monster:${monster.id}`, icon: 'chat', label: `Parler à ${monster.name}` };
    // Un décor muet : rien à faire, pas même avec l'objet derrière lui.
    if (monster) return null;
    // Son {monstre} suiveur, derrière lui : le joueur s'est retourné pour le regarder.
    const { follower, trail } = self;
    if (follower && !self.surf && trail.x === x && trail.y === y) return { key: PET_PROMPT, icon: 'heart', label: `Caresser ${follower.name}` };
    const inspected = this.inspectPrompt(map, x, y);
    if (inspected) return inspected;
    // Interrupteur à actionner en face (comme sur le serveur, il passe avant ce que l'objet fait d'ordinaire).
    const lever = switchesAt(map.objects, this.elements, { x, y }, 'interact').at(-1);
    if (lever) return { key: `switch:${lever}`, icon: 'switch', label: 'Actionner' };
    const scene = this.sceneOf(map);
    const object = scene.cells.blocked.has(y * map.width + x) ? objectAt(map.objects, this.elements, x, y) : undefined;
    const obstacle = object && this.elements.get(object.elementId)?.obstacle;
    // L'action affichée est celle de la capacité (onglet Configuration).
    const { abilities } = gameConfig();
    if (object && obstacle === 'clear') return { key: `obstacle:${object.x}:${object.y}`, icon: 'sword', label: abilities.clear.verb };
    if (!self.surf && isSurfable({ width: map.width, height: map.height, cells: scene.cells }, { x, y })) return { key: `surf:${x}:${y}`, icon: 'type-water', label: abilities.surf.verb };
    const interaction = this.sceneOf(map).cells.interactions.get(y * map.width + x);
    if (interaction === 'use') return this.usePrompt(map, x, y);
    return interaction ? { key: interaction, ...INTERACTIONS[interaction], label: creatureText(INTERACTIONS[interaction].label) } : null;
  }

  /** Objet posé à utiliser en face (« Utiliser Capsule ») : seulement si une quête en cours le demande, sinon c'est un décor. */
  private usePrompt(map: MapView, x: number, y: number): PromptView | null {
    for (const object of map.objects) {
      const element = this.elements.get(object.elementId);
      if (element?.interaction !== 'use' || !object.use?.item || !map.usable.includes(object.use.id)) continue;
      const item = itemCatalog.get(object.use.item);
      const faced = [...coveredCells(object, element)].some((cell) => cell.x === x && cell.y === y && element.collision[cell.cell]);
      if (item && faced) return { key: `use:${object.use.id}`, icon: INTERACTIONS.use.icon, label: `Utiliser ${item.name}` };
    }
    return null;
  }

  /**
   * Objet posé à examiner en face : si une quête en cours le demande, ou s'il a un message (sinon, rien). Comme sur le
   * serveur, la quête passe avant le reste, puis le message.
   */
  private inspectPrompt(map: MapView, x: number, y: number): PromptView | null {
    const find = (accept: (object: MapObject, element: MapElement) => boolean) => inDrawingOrder(map.objects).reverse().find((object) => {
      const element = this.elements.get(object.elementId);
      return element?.inspectable && accept(object, element) && [...coveredCells(object, element)].some((cell) => cell.x === x && cell.y === y);
    });
    const object = find((candidate) => map.inspectable.includes(objectSpotKey(candidate))) ?? find(hasInspectMessage);
    const element = object && this.elements.get(object.elementId);
    return object && element ? { key: `inspect:${objectSpotKey(object)}`, icon: 'info', label: `Examiner ${element.name}` } : null;
  }

  private refreshHud(): void {
    const world = this.world;
    if (!world) return;
    const { map, self, zoneProgress } = world;
    $('#map-name').textContent = map.name;
    const zoneId = map.zones[self.y * map.width + self.x] ?? 0;
    const zoneLabel = $('#zone-name');
    zoneLabel.hidden = !zoneId;
    if (!zoneId) return;

    // « 🌿 Hautes herbes · ◉ 2/5 » : espèces de la zone déjà capturées.
    const name = map.zoneNames[zoneId] ?? 'Zone de farm';
    const progress = zoneProgress[zoneId];
    const complete = progress !== undefined && progress.total > 0 && progress.caught === progress.total;
    zoneLabel.classList.toggle('complete', complete);
    zoneLabel.title = progress?.total ? 'Espèces de cette zone déjà capturées' : '';
    zoneLabel.replaceChildren(icon('zone'), ` ${name}`);
    const boost = map.zoneShinyBoosts[zoneId];
    if (boost) {
      zoneLabel.append(el('span', { className: 'zone-shiny', title: `Chromatiques ${boost} fois plus fréquents ici` }, icon('sparkles'), ` ×${boost}`));
    }
    if (progress?.total) {
      zoneLabel.append(el('span', { className: 'zone-progress' }, icon(complete ? 'check' : 'capture'), ` ${progress.caught}/${progress.total}`));
    }
    // Icônes des conditions (moment, météo) auxquelles certaines espèces de la zone sont réservées.
    if (progress) {
      const hints = [
        ...progress.times.map((time) => ({ name: TIME_HINTS[time].icon, label: `Certains monstres n'apparaissent que ${TIME_HINTS[time].label}` })),
        ...progress.weathers.map((weather) => ({ name: WEATHER_HINTS[weather].icon, label: `Certains monstres n'apparaissent que ${WEATHER_HINTS[weather].label}` })),
      ].filter((hint, index, all) => all.findIndex((other) => other.name === hint.name) === index);
      if (hints.length) zoneLabel.append(el('span', { className: 'zone-conditions' }, ...hints.map((hint) => icon(hint.name, { label: hint.label }))));
    }
  }
}
